import { FC, useMemo, useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import { useGuardianState } from '../../../../contexts/guardian.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useAcademicsFiltersContext } from '../../../../../academics/contexts/academicsFilters.context';
import { useAcademicsMaterialsHook } from '../../../../../academics/hooks/useAcademicsMaterialsHook';
import PaginatedGrid from '../../../../../../components/paginatedGrid/PaginatedGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  IAcademicsMaterials,
  MaterialCategory,
  MaterialStatus,
  MaterialCondition,
} from '../../../../../../../apis/academics/academicsMaterials.interfaces';
import { MenuBook, Science, Computer } from '@mui/icons-material';

const getMaterialIcon = (category: MaterialCategory) => {
  switch (category) {
    case MaterialCategory.BOOK:
      return <MenuBook fontSize="small" />;
    case MaterialCategory.LAB_EQUIPMENT:
      return <Science fontSize="small" />;
    case MaterialCategory.DIGITAL_RESOURCE:
      return <Computer fontSize="small" />;
    default:
      return <MenuBook fontSize="small" />;
  }
};

const GuardianMaterialsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedChildId } = useGuardianState();
  const { academicYear, grade } = useAcademicsFiltersContext();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { data, isLoading, error } = useAcademicsMaterialsHook({
    page,
    limit: pageSize,
    ...(academicYear && { academicYear }),
    ...(grade && { grade }),
    ...(selectedChildId && { assignedToStudentIds: [selectedChildId] }),
  });

  const materials: IAcademicsMaterials[] = useMemo(
    () => data?.materials || [],
    [data?.materials]
  );
  const totalCount = data?.total || 0;

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'name',
        headerName: translateText('guardian.academics.materials.columns.name'),
        flex: 1,
        minWidth: 200,
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getMaterialIcon(params.row.category)}
            <Typography variant="body2" fontWeight="medium">
              {params.value}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'category',
        headerName: translateText('guardian.academics.materials.columns.category'),
        width: 150,
      },
      {
        field: 'department',
        headerName: translateText('guardian.academics.materials.columns.department'),
        width: 120,
      },
      {
        field: 'status',
        headerName: translateText('guardian.academics.materials.columns.status'),
        width: 120,
        renderCell: (params: GridRenderCellParams) => {
          const status = params.value as MaterialStatus;
          const statusColors: Record<
            MaterialStatus,
            'default' | 'success' | 'warning' | 'error' | 'info'
          > = {
            [MaterialStatus.AVAILABLE]: 'success',
            [MaterialStatus.IN_USE]: 'info',
            [MaterialStatus.MAINTENANCE]: 'warning',
            [MaterialStatus.RETIRED]: 'default',
            [MaterialStatus.LOST]: 'error',
            [MaterialStatus.DAMAGED]: 'error',
          };
          return (
            <Chip
              label={status}
              color={statusColors[status] || 'default'}
              size="small"
            />
          );
        },
      },
      {
        field: 'condition',
        headerName: translateText('guardian.academics.materials.columns.condition'),
        width: 120,
        renderCell: (params: GridRenderCellParams) => {
          const condition = params.value as MaterialCondition;
          return (
            <Chip label={condition} color="default" size="small" variant="outlined" />
          );
        },
      },
    ],
    [translateText]
  );

  const rows = useMemo(
    () =>
      materials.map((m) => ({
        id: m.id,
        name: m.name,
        category: m.category,
        department: m.department,
        status: m.status,
        condition: m.condition,
      })),
    [materials]
  );

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  if (!selectedChildId) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-materials-container">
        <Grid item xs={12}>
          <Alert severity="info">{translateText('guardian.selectChild')}</Alert>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-materials-container">
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {translateText('common.loading')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-materials-container">
        <Grid item xs={12}>
          <Alert severity="error">{translateText('common.error')}</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-materials-container">
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 2 }} data-testid="guardian-materials-title">
          {translateText('guardian.academics.materials.title')}
        </Typography>
      </Grid>
      {materials.length === 0 ? (
        <Grid item xs={12}>
          <Alert severity="info" data-testid="guardian-materials-empty">
            {translateText('guardian.academics.materials.noMaterials')}
          </Alert>
        </Grid>
      ) : (
        <Grid item xs={12}>
          <PaginatedGrid
            rows={rows}
            columns={columns}
            loading={isLoading}
            dataTestId="guardian-materials-grid"
            serverSide={true}
            totalCount={totalCount}
            currentPage={page}
            currentPageSize={pageSize}
            onPageChange={handlePageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
            hideColumnsOnMobile={['department', 'condition']}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default GuardianMaterialsView;
